// The Certify message catalog (docs/copy/slice4-certify-copy.md - the copywriter's
// sole-writer, DRAFT copy source for Slice 4). Every string below is copied VERBATIM
// from that file, keyed exactly as it names them there; this module never authors or
// edits a word (`app-engineering`/`CLAUDE.md` §8 - copy is the copywriter's + counsel's
// lane, not this build lane's). Every line the copy file tags
// `[FLAGGED: domain-expert/counsel review before launch - not final]` is DRAFT and is
// rendered per the copy file's own instruction ("render it, but it is not final") -
// this module renders it; it never invents or finalizes wording (BUILD-SPEC §6.6/§8).
//
// SCOPED, ENGLISH-ONLY, LOCAL TO apps/web - flagged, not a platform i18n system.
// No message-catalog/i18n mechanism exists anywhere in this repo yet (verified: no
// i18next/useTranslation/ICU import anywhere under apps/web or docs) - Slice 2/3 screens
// hardcode English strings directly in JSX. Certify's own copy file ships as KEYED
// strings this slice and explicitly asks the build to "use the keyed strings", so this
// module is the minimal mechanism that satisfies that without inventing the real,
// locale-routed ICU MessageFormat system - that is the i18n-engineer's lane
// (`i18n-localization`). `certifyCopy()` below does plain `{token}` substitution only
// (no ICU plural rules - the copy file's own §6 implementer note names this exact gap
// per language); every English row above is reproduced as-is, including any string that
// reads ungrammatically for a count other than one (the copy file does not resolve that
// either - fixing it here would be inventing wording this lane does not own).
//
// CORPUS-SOURCED entries are deliberately OMITTED (a framework's one-line description,
// a control's statement text) - the copy file itself says these render as DATA from the
// compliance corpus fetch, never as a fixed English string in this catalog
// (slice4-certify-copy.md §1.5/§1.6, §6 "corpus-sourced text stays out of this file").
// The public trust page's own "Live status" section (§3.2) is out of scope: ADR-0020
// decision 5 serves the real public page from `apps/api`, and the mockup's live-status
// board has no backing field on the landed `TrustPageView` this slice
// (docs/design/slice4-certify-ux.md §6.1/§6.2/§9.12) - so those keys are not reproduced
// here; nothing in apps/web renders them.
export const CERTIFY_COPY = {
  // ---- 1.1 Header and status chip ----
  "certify.dashboard.title": "Certify",
  "certify.dashboard.subtitle": "The checks behind {appName}'s security and privacy posture, run continuously.",
  "certify.dashboard.statusChip.certified": "Certified, valid to {validUntilDate}",
  "certify.dashboard.statusChip.dulled": "Badge dulled: {failingCount} check needs attention",
  "certify.dashboard.statusChip.notYetCertified": "Not certified yet. Run your first Certify pass.",
  "certify.dashboard.statusChip.assessing": "Checking your posture...",

  // ---- 1.2 Scanners panel ----
  "certify.dashboard.scanners.heading": "Scanners",
  "certify.dashboard.scanners.sast.name": "Static analysis",
  "certify.dashboard.scanners.sast.detail": "{highCount} high, {lowCount} low, {waivedCount} waived",
  "certify.dashboard.scanners.secretScan.name": "Secret scan",
  "certify.dashboard.scanners.secretScan.detailPass": "No secrets found in code or history",
  "certify.dashboard.scanners.secretScan.detailFail": "{foundCount} secret found. Rotate it now.",
  "certify.dashboard.scanners.dependencies.name": "Dependencies",
  "certify.dashboard.scanners.dependencies.detail": "{patchedCount} patched this shift, {openCount} open",
  "certify.dashboard.scanners.tag.pass": "Pass",
  // W8 fix (design-review): was "{count} action" - a clipped fragment with no verb,
  // read like a truncation bug. No real per-language plural exists in this lane's
  // plain `{token}` substitution (see this file's own header note); this is the
  // literal fix named in the review ("1 action needed"), not a new plural mechanism.
  "certify.dashboard.scanners.tag.actionNeeded": "{count} action needed",
  "certify.dashboard.scanners.tag.fail": "Fail",
  "certify.dashboard.scanners.tag.running": "Running...",
  "certify.dashboard.scanners.tag.error": "Couldn't run",

  // ---- 1.3 Builder health checks ----
  "certify.dashboard.builderHealth.heading": "Builder health",
  "certify.dashboard.builderHealth.subtitle": "The checks that catch a first-customer bug before your customer does.",
  "certify.dashboard.builderHealth.email.name": "Email deliverability",
  "certify.dashboard.builderHealth.email.detailPass": "SPF, DKIM, and DMARC all pass",
  "certify.dashboard.builderHealth.email.detailFail": "{recordName} record missing or misconfigured",
  "certify.dashboard.builderHealth.email.fixCta": "Fix email setup",
  "certify.dashboard.builderHealth.domainSsl.name": "Domain and SSL",
  "certify.dashboard.builderHealth.domainSsl.detailPass": "Certificate valid, renews in {daysUntilExpiry} days",
  "certify.dashboard.builderHealth.domainSsl.detailWarn": "Certificate expires in {daysUntilExpiry} days. Renew it now.",
  "certify.dashboard.builderHealth.domainSsl.detailFail": "Certificate expired {daysExpired} days ago",
  "certify.dashboard.builderHealth.domainSsl.fixCta": "Renew certificate",
  "certify.dashboard.builderHealth.payments.name": "Payments",
  "certify.dashboard.builderHealth.payments.detailPass": "Stripe connected and processing",
  "certify.dashboard.builderHealth.payments.detailFail": "Stripe {issueType}. Customers can't pay until this clears.",
  "certify.dashboard.builderHealth.payments.fixCta": "Fix payments",
  "certify.dashboard.builderHealth.notYetChecked": "Not yet checked",
  // W1 fix (design-review): rows were printing the raw lowercase `HealthStatus` enum
  // ("ok"/"warning"/"fail", packages/shared/src/model/certify.ts:206) verbatim - an
  // internal wire token leaking to a founder-facing row. Plain structural labels, same
  // "bare label, not a compliance claim" precedent as the rest of this section (§0
  // header) - not individually [FLAGGED].
  "certify.dashboard.builderHealth.status.ok": "Passing",
  "certify.dashboard.builderHealth.status.warning": "Needs attention",
  "certify.dashboard.builderHealth.status.fail": "Failing",

  // ---- 1.4 GDPR posture panel ----
  "certify.dashboard.gdpr.heading": "GDPR posture",
  "certify.dashboard.gdpr.subtitle": "Nightcrew's self-assessment of {appName} against GDPR's published rules. Not legal advice, and not a determination by a supervisory authority.",
  "certify.dashboard.gdpr.actionNeeded": "{actionCount} item needs your sign-off before this posture updates.",
  "certify.dashboard.gdpr.signOffCta": "Review and sign off",
  "certify.dashboard.gdpr.needsDecision": "Needs your decision",

  // ---- 1.5 Framework catalog ----
  "certify.dashboard.frameworks.heading": "Check against a framework",
  "certify.dashboard.frameworks.subtitle": "GDPR runs on every app. Add a framework below to check {appName}'s posture against it too.",
  "certify.dashboard.frameworks.selectCta": "Check against {frameworkName}",
  "certify.dashboard.frameworks.disclaimerInline": "Every result here is Nightcrew's own readiness check, not an official certification.",
  "certify.dashboard.frameworks.alwaysIncluded": "Always included",

  // ---- 1.6 Framework result detail ----
  "certify.dashboard.frameworkResult.heading": "{frameworkName} readiness",
  "certify.dashboard.frameworkResult.qualifiesYes": "Self-assessed as ready: {passCount} of {totalCount} controls pass.",
  "certify.dashboard.frameworkResult.qualifiesPartial": "Self-assessed as partly ready: {passCount} of {totalCount} controls pass.",
  "certify.dashboard.frameworkResult.qualifiesNo": "Self-assessed as not ready: {passCount} of {totalCount} controls pass.",
  "certify.dashboard.frameworkResult.controlRow.tagPass": "Pass",
  "certify.dashboard.frameworkResult.controlRow.tagFail": "Fail",
  "certify.dashboard.frameworkResult.controlRow.tagNeedsEvidence": "Needs evidence",
  "certify.dashboard.frameworkResult.lastAssessed": "Last checked {assessedAtDate} against corpus version {corpusVersion}.",
  "certify.dashboard.frameworkResult.provisional": "Preliminary result, pending the full standard.",
  "certify.dashboard.frameworkResult.reassessCta": "Check again",
  "certify.dashboard.frameworkResult.gapToShiftNote": "A failing control becomes a fix your crew can pick up.",

  // ---- 1.7 Certificate upload ----
  "certify.dashboard.certificate.heading": "Hold the real certificate?",
  "certify.dashboard.certificate.subtitle": "Upload it. Nightcrew checks the file and its expiry date, not the auditor's findings.",
  "certify.dashboard.certificate.uploadCta": "Upload certificate",
  "certify.dashboard.certificate.field.issuer": "Issuer",
  "certify.dashboard.certificate.field.scope": "Scope",
  "certify.dashboard.certificate.field.issuedDate": "Issued",
  "certify.dashboard.certificate.field.expiryDate": "Expires",
  "certify.dashboard.certificate.verifiedTag": "Verified upload",
  "certify.dashboard.certificate.verifiedDetail": "Issued by {issuer}, {scope}, valid {issuedDate} to {expiryDate}. Nightcrew checked the file and these dates; {issuer} issued the certificate itself.",
  "certify.dashboard.certificate.expiringSoon": "This certificate expires in {daysUntilExpiry} days.",
  "certify.dashboard.certificate.expired": "This certificate expired {daysExpired} days ago. Remove it or upload the renewal.",

  // ---- 1.8 Dashboard banner ----
  "certify.dashboard.banner.allClear": "Every check is passing right now.",
  "certify.dashboard.banner.actionNeeded": "{count} check needs your attention before the badge updates.",
  "certify.dashboard.banner.signOffCta": "Sign off to renew the badge",

  // ---- 2. Data room ----
  "certify.dataRoom.heading": "Data room",
  "certify.dataRoom.subtitle": "The documents your customers and investors ask for, generated from {appName}'s real posture and kept current.",
  "certify.dataRoom.shareLinkCta": "Copy share link",
  "certify.dataRoom.shareLinkCopied": "Link copied",
  "certify.dataRoom.tag.ready": "Ready",
  "certify.dataRoom.tag.review": "Review",
  "certify.dataRoom.tag.generating": "Generating...",
  "certify.dataRoom.disclosureFooter": "These documents reflect Nightcrew's self-assessment and {appName}'s current setup. They are not legal advice and do not replace counsel's review before you rely on them externally.",
  "certify.dataRoom.privacyNotice": "Stored privately and encrypted. Only your crew and your team can see it.",
  "certify.dataRoom.dropInstruction": "Drop a file here, or choose one from your computer.",
  "certify.dataRoom.dragOver": "Drop to upload",
  "certify.dataRoom.chooseFileCta": "Choose a file",
  "certify.dataRoom.uploading": "Uploading {filename}...",
  "certify.dataRoom.scanning": "Checking the file before we store it...",
  "certify.dataRoom.storedUnverified": "Added. Shown as unverified until we verify it.",
  "certify.dataRoom.addedConfirmation": "{filename} added to the data room.",
  "certify.dataRoom.verifyState.unverified": "Not yet verified",
  "certify.dataRoom.verifyState.verifying": "Verifying...",
  "certify.dataRoom.verifyState.verified": "Verified",
  "certify.dataRoom.verifyState.failed": "Verification failed",
  "certify.dataRoom.expiryWatch.expiresIn": "Expires in {days} days",
  "certify.dataRoom.expiryWatch.expired": "Expired {date}",
  "certify.dataRoom.evidence.shareLinkCta": "Share link",
  "certify.dataRoom.evidence.downloadCta": "Download",
  "certify.dataRoom.evidence.shareLinkNote": "Anyone with this link can view this document until you revoke it.",
  "certify.dataRoom.evidence.shareLinkGeneratedCta": "Copy link",
  "certify.dataRoom.evidence.notFound": "We can't find that document.",

  // ---- 3. Trust page and badge ----
  "certify.trustPage.nav.lastChecked": "Last checked {lastCheckedDate}",
  "certify.trustPage.nav.badgeInline": "Nightcrew Certified",
  "certify.trustPage.hero.eyebrow": "Trust and status",
  "certify.trustPage.hero.heading": "{appName}'s security and privacy posture, checked continuously.",
  "certify.trustPage.hero.body": "Every check below runs against real evidence and refreshes automatically. This is Nightcrew's self-assessment, not a certification from an accrediting body.",
  "certify.trustPage.hero.residencyPill": "EU data residency",
  "certify.trustPage.security.heading": "Security",
  "certify.trustPage.security.item.encryption": "Data encrypted in transit and at rest",
  "certify.trustPage.security.item.vaultedSecrets": "Secrets held in a vault, never in code or logs",
  "certify.trustPage.security.item.scannedChanges": "Every change scanned before release",
  "certify.trustPage.security.item.secondModelReview": "A second model reviews high-risk changes",
  "certify.trustPage.privacy.heading": "Privacy and data protection",
  "certify.trustPage.privacy.item.euResidency": "Data stored in the EU",
  "certify.trustPage.privacy.item.subjectRights": "Subject access and erasure requests supported",
  "certify.trustPage.privacy.item.dpaOnRequest": "Data processing agreement available on request",
  "certify.trustPage.privacy.item.subprocessorsCurrent": "Subprocessors listed and kept current",
  "certify.trustPage.footer.autoChecked": "Nightcrew checks this page against {appName}'s real posture automatically.",
  "certify.trustPage.footer.tamperNote": "If this page or the badge is altered outside Nightcrew, the badge stops validating.",

  // W4 fix (design-review, PUBLIC-page severity): `TrustClaim.source` is a free-form
  // wire token shaped `<kind>:<key>` (e.g. "scan:secret_scan", "assessment:gdpr-2026.1" -
  // packages/shared/src/model/certify.ts:251, apps/web/src/testing/certify-fixtures.ts:
  // 150,164), never itself human copy - it was rendering VERBATIM on a founder's
  // public-facing trust-page preview. Human labels for the token's `<kind>` prefix; a
  // plain structural label, not a new compliance claim (same §0-header precedent).
  "certify.trustPage.claimSource.scan": "Automated security scan",
  "certify.trustPage.claimSource.assessment": "Nightcrew self-assessment",
  "certify.trustPage.claimSource.config": "System configuration check",
  "certify.trustPage.claimSource.fallback": "Nightcrew automated check",

  // ---- 3.6 Badge and seal ----
  "certify.badge.sealAlt.certified_current": "Nightcrew Certified badge for {appName}: currently certified",
  "certify.badge.sealAlt.dulled": "Nightcrew Certified badge for {appName}: dulled, a check needs attention",
  "certify.badge.sealAlt.unverified": "Nightcrew Certified badge for {appName}: not yet certified",
  "certify.badge.sealAlt.expired": "Nightcrew Certified badge for {appName}: certificate expired",
  "certify.badge.label.certified_current": "Certified",
  "certify.badge.label.dulled": "Badge dulled",
  "certify.badge.label.unverified": "Not yet certified",
  "certify.badge.label.expired": "Expired",
  "certify.badge.inline.certified": "Nightcrew Certified",
  "certify.badge.description": "Pass every Certify check and the badge unlocks. It links to {appName}'s live trust page, so a customer can check it in one click. It dulls the moment a check fails, so it never shows a stale pass.",
  "certify.badge.tooltip.trigger": "What does this mean?",
  "certify.badge.dulled.dashboardLabel": "Badge dulled",
  "certify.badge.dulled.dashboardDetail": "{failingCount} check needs attention. The badge stays dulled until every check passes again.",
  "certify.badge.dulled.publicNote": "This badge is temporarily dulled while {appName} resolves an open check.",
  "certify.badge.expired.publicNote": "Verification has lapsed. {appName}'s certificate expired and needs to be renewed.",
  "certify.badge.embed.heading": "Drop it anywhere",
  "certify.badge.embed.copyCta": "Copy embed",
  "certify.badge.embed.downloadCta": "Download SVG and PNG",
  "certify.badge.embed.copiedConfirmation": "Copied",

  // ---- 4. The self-assessment disclosure block (D46) ----
  "certify.disclosure.short":
    "Nightcrew Certified reflects Nightcrew's own automated self-assessment. It is not an official certification from an accrediting or regulatory body.",
  "certify.disclosure.medium":
    "This result is Nightcrew's self-assessment of {appName} against {frameworkName}'s published rules. It checks the app's own code, configuration, and data map against Nightcrew's control library. It is not an official certification, audit, or determination by {frameworkName}'s accrediting body or any regulator, and a pass here does not guarantee the app would pass a formal audit.",
  "certify.disclosure.long":
    "Nightcrew Certify runs an automated, self-assessed readiness check against the published rules of each framework a founder selects. GDPR posture runs on every app; SOC 2, ISO 27001, NIS2, HIPAA, and other frameworks are optional additions from Nightcrew's maintained catalog. Every result traces to Nightcrew's own control library, kept current as the underlying rules change, and is not an official certification, accreditation, or legal determination. It does not replace a formal audit, a licensed assessor, or counsel's review. Where a founder holds a real certificate from an accredited body, Nightcrew stores it as a verified upload: Nightcrew checks the uploaded file and its stated issuer, scope, and expiry date, and tracks the expiry so it does not lapse silently. Nightcrew does not audit, certify, or vouch for the issuing body's findings.",
  "certify.disclosure.certificateDistinction":
    "A \"verified\" credential means Nightcrew checked the uploaded file and its expiry date. It does not mean Nightcrew certifies that {appName} meets any standard. Only {issuer} can make that determination.",

  // ---- 5. Error and empty states ----
  "certify.errors.scanner.timedOut": "The {scannerName} scan didn't finish. Try running it again.",
  "certify.errors.scanner.timedOut.cta": "Retry scan",
  "certify.errors.scanner.failed": "Something went wrong running this scan. Nothing was skipped or waived. Try again, or ask your crew if it keeps happening.",
  "certify.errors.scanner.failed.cta": "Ask your crew",
  "certify.errors.certificate.wrongFileType": "Certify accepts a PDF, PNG, or JPG for a certificate. Upload one of those.",
  "certify.errors.certificate.tooLarge": "That file is over {maxSizeMb} MB. Compress it or upload a smaller copy.",
  "certify.errors.certificate.failedScan": "Nightcrew couldn't confirm this file is safe to store. Upload a different copy, or ask your crew.",
  "certify.errors.certificate.missingExpiry": "Add the certificate's expiry date before you upload it. Nightcrew tracks it so it never lapses silently.",
  "certify.errors.certificate.alreadyExpired": "This certificate's expiry date is in the past. Upload the current certificate, or fix the date if it's wrong.",
  "certify.errors.framework.notEnoughEvidence": "Certify can't check {controlName} yet. {appName} doesn't have enough evidence connected for this control. Connect {missingSource} to check it.",
  "certify.errors.framework.corpusUpdating": "{frameworkName}'s rules just updated. Re-checking every affected control now.",
  "certify.errors.framework.assessmentFailed": "Something went wrong checking {frameworkName}. Nothing was marked pass or fail. Try again.",
  "certify.empty.dataRoom.firstRun.heading": "Your data room is empty",
  "certify.empty.dataRoom.firstRun.body": "Run your first Certify pass and every document generates from {appName}'s real setup.",
  "certify.empty.dataRoom.firstRun.cta": "Run Certify",
  "certify.empty.dataRoom.docGenerating": "Generating from your latest posture...",
  "certify.empty.dataRoom.docFailed": "This document didn't generate. Retry, or ask your crew.",
  "certify.empty.dataRoom.docFailed.cta": "Retry",
  "certify.empty.trustPage.notPublished.heading": "Your trust page isn't public yet",
  "certify.empty.trustPage.notPublished.body": "Publish it once your first Certify pass clears, so customers and investors see real evidence, not a placeholder.",
  "certify.empty.trustPage.notPublished.cta": "Review and publish",
  "certify.empty.badge.locked": "The badge unlocks once every check passes.",
  "certify.empty.notEntitled.heading": "Certify is a Studio feature",
  "certify.empty.notEntitled.body": "Move to Studio to run Certify, publish a trust page, and unlock the badge for {appName}.",
  "certify.empty.notEntitled.cta": "Upgrade to Studio",
  "certify.errors.generic.title": "Something went wrong on our end.",
  "certify.errors.generic.body": "Nothing changed and nothing was skipped.",
  "certify.errors.generic.retryCta": "Try again",

  // ---- Additional structural strings this build needs, same DRAFT/flagged status ----
  // (not individually itemized in slice4-certify-copy.md, but the same "render the
  // draft, flag it" discipline applies - each is a plain structural label, not a new
  // compliance/posture CLAIM, so it carries no [FLAGGED] tag of its own per that file's
  // own bare-label convention, §0 header.)
  "certify.dashboard.emptyNeverRun.heading": "We haven't run Certify for {appName} yet.",
  "certify.dashboard.emptyNeverRun.body": "It checks your security posture, compliance readiness, and builder health, and keeps a public trust page current.",
  "certify.dashboard.emptyNeverRun.cta": "Start Certify",
  "certify.dashboard.scanning.readout": "Checking {done} of {total}...",
  "certify.dashboard.scanning.body": "Your crew is checking {appName}'s posture. This can take a few minutes.",
  "certify.dashboard.section.scanners": "Scanners",
  "certify.dashboard.section.frameworks": "Framework self-assessment",
  "certify.dashboard.section.builderHealth": "Builder health",
  "certify.dashboard.atAGlance.heading": "Certify at a glance",
  "certify.dashboard.atAGlance.lastRun": "Last run {date}",
  "certify.dashboard.atAGlance.neverRun": "Never run",
  "certify.dashboard.dataRoomLinkCta": "Open the data room",
  "certify.dashboard.trustPageLinkCta": "Manage the trust page",
  "certify.dashboard.gdprRouting.bannerCount": "{count} item needs your decision",
  "certify.dashboard.gdprRouting.openCta": "Open",
  "certify.dashboard.gdprRouting.panelWhy": "This touches personal data, so it always waits for you.",
  "certify.dashboard.gdprRouting.confirmCta": "Confirm",
  "certify.dashboard.gdprRouting.notNowCta": "Not now",
  "certify.dashboard.gdprRouting.confirmedToast": "Confirmed. Thanks - this posture updates on the next check.",
  "certify.stageGate.heading": "Certify becomes available once {appName} reaches MVP.",
  "certify.stageGate.body": "Keep building, and this unlocks automatically once the app moves past Validated.",
  "certify.stageGate.buildLinkCta": "Go to Build",
  "certify.trustPage.management.heading": "Trust page",
  "certify.trustPage.management.previewBadge": "Preview - not public yet",
  "certify.trustPage.management.notReadyBody": "Run Certify at least once before publishing a trust page.",
  "certify.trustPage.management.notReadyCta": "Start Certify",
  "certify.trustPage.management.publishCta": "Publish trust page",
  "certify.trustPage.management.unpublishCta": "Unpublish",
  "certify.trustPage.management.publishConfirmBody": "This makes {subset} visible at {url} to anyone with the link.",
  "certify.trustPage.management.publishConfirmCta": "Publish",
  "certify.trustPage.management.publishedStatus": "Published",
  "certify.trustPage.management.unpublishedStatus": "Your trust page is private again.",
  "certify.trustPage.management.publicUrlLabel": "Public URL",
  "certify.trustPage.management.copyUrlCta": "Copy link",
  "certify.trustPage.management.chooseFeaturedFramework": "Feature one framework's readiness publicly",
  "certify.trustPage.management.chooseFeaturedCertificate": "Feature one verified certificate publicly",
  "certify.trustPage.management.noneOption": "None",
  "certify.trustPage.management.notEligibleNote": "Only a framework self-assessed as ready can be featured publicly.",
} as const;

export type CertifyCopyKey = keyof typeof CERTIFY_COPY;

/**
 * Plain `{token}` substitution only - no ICU plural rules (the copy file's own §6
 * implementer note: real per-language plurals are the i18n-engineer's lane). A missing
 * var leaves the literal `{token}` in place rather than throwing, so a caller mistake is
 * visible in the render instead of a crash.
 */
export const certifyCopy = (
  key: CertifyCopyKey,
  vars?: Record<string, string | number>,
): string => {
  const template = CERTIFY_COPY[key];
  if (vars === undefined) return template;
  return template.replace(/\{(\w+)\}/g, (match: string, name: string) => {
    const value = vars[name];
    return value === undefined ? match : String(value);
  });
};
